import { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { DateAutoInput, formatDateForDisplay } from "../components/DateAutoInput";
import {
  HandCoins, Plus, Pencil, Trash2, Loader2, Search,
  FileText, Receipt, Users, ArrowUpDown
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const MONTHS = [
  "Ianuarie", "Februarie", "Martie", "Aprilie", "Mai", "Iunie",
  "Iulie", "August", "Septembrie", "Octombrie", "Noiembrie", "Decembrie"
];

const formatMonth = (luna) => {
  if (!luna) return "-";
  const [year, month] = luna.split("-");
  const idx = parseInt(month, 10) - 1;
  return `${MONTHS[idx] || month} ${year}`;
};

const initialForm = {
  avocat_id: "",
  suma: "",
  data_retrocedare: "",
  luna_retrocedare: "",
  activitati: "",
  factura_numar: "",
  factura_data: "",
  factura_valoare: "",
  chitanta_numar: "",
  chitanta_data: "",
  chitanta_valoare: "",
};

export default function RetrocedariPage() {
  const { getAuthHeader } = useAuth();
  const [retrocedari, setRetrocedari] = useState([]);
  const [colaboratori, setColaboratori] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterAvocat, setFilterAvocat] = useState("all");
  const [sortBy, setSortBy] = useState("date");

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [retRes, colRes] = await Promise.all([
        axios.get(`${API}/api/retrocedari`, getAuthHeader()),
        axios.get(`${API}/api/retrocedari/colaboratori`, getAuthHeader()),
      ]);
      setRetrocedari(retRes.data);
      setColaboratori(colRes.data);
    } catch (error) {
      toast.error("Eroare la încărcarea datelor");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Totals
  const totals = useMemo(() => {
    let totalSuma = 0, totalFacturi = 0, totalChitante = 0;
    retrocedari.forEach(r => {
      totalSuma += r.suma || 0;
      totalFacturi += r.factura_valoare || 0;
      totalChitante += r.chitanta_valoare || 0;
    });
    return { totalSuma, totalFacturi, totalChitante, count: retrocedari.length };
  }, [retrocedari]);

  // Filter + Sort
  const filteredItems = useMemo(() => {
    let result = [...retrocedari];
    if (filterAvocat !== "all") {
      result = result.filter(r => r.avocat_id === filterAvocat);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(r =>
        (r.avocat_name || "").toLowerCase().includes(q) ||
        (r.activitati || "").toLowerCase().includes(q) ||
        (r.factura_numar || "").toLowerCase().includes(q) ||
        (r.chitanta_numar || "").toLowerCase().includes(q)
      );
    }
    if (sortBy === "date") {
      result.sort((a, b) => (b.data_retrocedare || "").localeCompare(a.data_retrocedare || ""));
    } else if (sortBy === "suma") {
      result.sort((a, b) => (b.suma || 0) - (a.suma || 0));
    } else if (sortBy === "avocat") {
      result.sort((a, b) => (a.avocat_name || "").localeCompare(b.avocat_name || "", "ro"));
    }
    return result;
  }, [retrocedari, filterAvocat, searchQuery, sortBy]);

  const handleOpenDialog = (item = null) => {
    if (item) {
      setSelectedItem(item);
      setFormData({
        avocat_id: item.avocat_id || "",
        suma: item.suma?.toString() || "",
        data_retrocedare: item.data_retrocedare || "",
        luna_retrocedare: item.luna_retrocedare || "",
        activitati: item.activitati || "",
        factura_numar: item.factura_numar || "",
        factura_data: item.factura_data || "",
        factura_valoare: item.factura_valoare?.toString() || "",
        chitanta_numar: item.chitanta_numar || "",
        chitanta_data: item.chitanta_data || "",
        chitanta_valoare: item.chitanta_valoare?.toString() || "",
      });
    } else {
      setSelectedItem(null);
      setFormData(initialForm);
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.avocat_id) {
      toast.error("Selectați avocatul colaborator");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        suma: parseFloat(formData.suma) || 0,
        factura_valoare: parseFloat(formData.factura_valoare) || 0,
        chitanta_valoare: parseFloat(formData.chitanta_valoare) || 0,
      };
      if (selectedItem) {
        await axios.put(`${API}/api/retrocedari/${selectedItem.id}`, payload, getAuthHeader());
        toast.success("Retrocedare actualizată");
      } else {
        await axios.post(`${API}/api/retrocedari`, payload, getAuthHeader());
        toast.success("Retrocedare adăugată");
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Eroare la salvare");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedItem) return;
    try {
      await axios.delete(`${API}/api/retrocedari/${selectedItem.id}`, getAuthHeader());
      toast.success("Retrocedare ștearsă");
      setDeleteDialogOpen(false);
      setSelectedItem(null);
      fetchData();
    } catch (error) {
      toast.error("Eroare la ștergere");
    }
  };

  // Generate month options (last 24 months)
  const monthOptions = useMemo(() => {
    const opts = [];
    const now = new Date();
    for (let i = 0; i < 24; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      opts.push({ value: val, label: formatMonth(val) });
    }
    return opts;
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="retrocedari-page">
      <div className="sticky top-0 z-10 bg-background pb-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2">
            <HandCoins className="w-7 h-7" />
            Retrocedări
          </h1>
          <p className="font-body text-muted-foreground mt-1">
            Evidența retrocedărilor către avocații colaboratori
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="add-retrocedare-btn">
          <Plus className="w-4 h-4 mr-2" />
          Adaugă Retrocedare
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="retrocedari-summary">
        <div className="rounded-xl border p-4">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Total Retrocedări</p>
          <p className="text-2xl font-bold mt-1">{totals.count}</p>
        </div>
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
          <p className="text-xs font-medium text-blue-600 uppercase tracking-wide">Total Sume</p>
          <p className="text-xl font-bold text-blue-800 mt-1 tabular-nums">{totals.totalSuma.toLocaleString("ro-RO", { minimumFractionDigits: 2 })} RON</p>
        </div>
        <div className="rounded-xl border border-green-200 bg-green-50 p-4">
          <p className="text-xs font-medium text-green-600 uppercase tracking-wide">Total Facturi</p>
          <p className="text-xl font-bold text-green-800 mt-1 tabular-nums">{totals.totalFacturi.toLocaleString("ro-RO", { minimumFractionDigits: 2 })} RON</p>
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <p className="text-xs font-medium text-amber-600 uppercase tracking-wide">Total Chitanțe</p>
          <p className="text-xl font-bold text-amber-800 mt-1 tabular-nums">{totals.totalChitante.toLocaleString("ro-RO", { minimumFractionDigits: 2 })} RON</p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Caută după avocat, activități, nr. factură..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="retrocedari-search"
          />
        </div>

        {colaboratori.length > 0 && (
          <Select value={filterAvocat} onValueChange={setFilterAvocat}>
            <SelectTrigger className="w-48" data-testid="filter-avocat-select">
              <Users className="w-3.5 h-3.5 mr-1.5" />
              <SelectValue placeholder="Toți avocații" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toți avocații</SelectItem>
              {colaboratori.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <div className="h-6 w-px bg-border" />

        {[
          { key: "date", label: "Dată" },
          { key: "suma", label: "Sumă" },
          { key: "avocat", label: "Avocat" },
        ].map(s => (
          <Button
            key={s.key}
            variant={sortBy === s.key ? "default" : "outline"}
            size="sm"
            onClick={() => setSortBy(s.key)}
          >
            <ArrowUpDown className="w-3.5 h-3.5 mr-1.5" />
            {s.label}
          </Button>
        ))}
      </div>
      </div>{/* End Sticky */}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {filteredItems.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <HandCoins className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>{retrocedari.length === 0 ? "Nu există retrocedări" : "Nu s-au găsit rezultate"}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="font-semibold">Avocat</TableHead>
                    <TableHead className="font-semibold">Luna</TableHead>
                    <TableHead className="font-semibold text-right">Sumă</TableHead>
                    <TableHead className="font-semibold">Data</TableHead>
                    <TableHead className="font-semibold">Activități</TableHead>
                    <TableHead className="font-semibold">Factură</TableHead>
                    <TableHead className="font-semibold">Chitanță</TableHead>
                    <TableHead className="font-semibold text-right">Acțiuni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredItems.map((item) => (
                    <TableRow key={item.id} data-testid={`retrocedare-row-${item.id}`}>
                      <TableCell className="font-medium">{item.avocat_name}</TableCell>
                      <TableCell>{formatMonth(item.luna_retrocedare)}</TableCell>
                      <TableCell className="text-right tabular-nums font-medium text-blue-700">
                        {(item.suma || 0).toLocaleString("ro-RO", { minimumFractionDigits: 2 })} RON
                      </TableCell>
                      <TableCell className="font-mono text-sm">{formatDateForDisplay(item.data_retrocedare)}</TableCell>
                      <TableCell className="max-w-[200px] truncate text-sm text-muted-foreground" title={item.activitati}>
                        {item.activitati || "-"}
                      </TableCell>
                      <TableCell className="text-sm">
                        {item.factura_numar ? (
                          <div>
                            <span className="font-mono">{item.factura_numar}</span>
                            <br />
                            <span className="text-xs text-muted-foreground">
                              {formatDateForDisplay(item.factura_data)} - {(item.factura_valoare || 0).toLocaleString("ro-RO", { minimumFractionDigits: 2 })} RON
                            </span>
                          </div>
                        ) : "-"}
                      </TableCell>
                      <TableCell className="text-sm">
                        {item.chitanta_numar ? (
                          <div>
                            <span className="font-mono">{item.chitanta_numar}</span>
                            <br />
                            <span className="text-xs text-muted-foreground">
                              {formatDateForDisplay(item.chitanta_data)} - {(item.chitanta_valoare || 0).toLocaleString("ro-RO", { minimumFractionDigits: 2 })} RON
                            </span>
                          </div>
                        ) : "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(item)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => { setSelectedItem(item); setDeleteDialogOpen(true); }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedItem ? "Editează Retrocedare" : "Adaugă Retrocedare"}</DialogTitle>
            <DialogDescription>
              {selectedItem ? "Modificați datele retrocedării" : "Completați datele noii retrocedări"}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-2">
            {/* Row 1: Avocat + Suma */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Avocat Colaborator *</Label>
                <Select value={formData.avocat_id} onValueChange={(val) => setFormData({ ...formData, avocat_id: val })}>
                  <SelectTrigger data-testid="select-avocat">
                    <SelectValue placeholder="Selectați avocatul" />
                  </SelectTrigger>
                  <SelectContent>
                    {colaboratori.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Suma Retrocedată (RON)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.suma}
                  onChange={(e) => setFormData({ ...formData, suma: e.target.value })}
                  placeholder="0.00"
                  data-testid="input-suma"
                />
              </div>
            </div>

            {/* Row 2: Data + Luna */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Data Retrocedării</Label>
                <DateAutoInput
                  value={formData.data_retrocedare}
                  onChange={(val) => setFormData({ ...formData, data_retrocedare: val })}
                  data-testid="input-data-retrocedare"
                />
              </div>
              <div className="space-y-2">
                <Label>Luna pentru care s-a făcut</Label>
                <Select value={formData.luna_retrocedare} onValueChange={(val) => setFormData({ ...formData, luna_retrocedare: val })}>
                  <SelectTrigger data-testid="select-luna">
                    <SelectValue placeholder="Selectați luna" />
                  </SelectTrigger>
                  <SelectContent>
                    {monthOptions.map((m) => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Row 3: Activitati */}
            <div className="space-y-2">
              <Label>Activitățile pentru care s-a retrocedat</Label>
              <Textarea
                value={formData.activitati}
                onChange={(e) => setFormData({ ...formData, activitati: e.target.value })}
                placeholder="Descrieți activitățile..."
                rows={3}
                data-testid="input-activitati"
              />
            </div>

            {/* Factura Section */}
            <Card className="bg-blue-50/50 border-blue-200">
              <CardHeader className="py-3">
                <CardTitle className="text-sm flex items-center gap-2 text-blue-800">
                  <FileText className="w-4 h-4" />
                  Factură emisă de colaborator
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Nr. Factură</Label>
                    <Input
                      value={formData.factura_numar}
                      onChange={(e) => setFormData({ ...formData, factura_numar: e.target.value })}
                      placeholder="Nr."
                      className="h-8 text-sm"
                      data-testid="input-factura-numar"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Data Factură</Label>
                    <DateAutoInput
                      value={formData.factura_data}
                      onChange={(val) => setFormData({ ...formData, factura_data: val })}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Valoare (RON)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.factura_valoare}
                      onChange={(e) => setFormData({ ...formData, factura_valoare: e.target.value })}
                      placeholder="0.00"
                      className="h-8 text-sm"
                      data-testid="input-factura-valoare"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Chitanta Section */}
            <Card className="bg-green-50/50 border-green-200">
              <CardHeader className="py-3">
                <CardTitle className="text-sm flex items-center gap-2 text-green-800">
                  <Receipt className="w-4 h-4" />
                  Chitanță emisă de colaborator
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Nr. Chitanță</Label>
                    <Input
                      value={formData.chitanta_numar}
                      onChange={(e) => setFormData({ ...formData, chitanta_numar: e.target.value })}
                      placeholder="Nr."
                      className="h-8 text-sm"
                      data-testid="input-chitanta-numar"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Data Chitanță</Label>
                    <DateAutoInput
                      value={formData.chitanta_data}
                      onChange={(val) => setFormData({ ...formData, chitanta_data: val })}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Valoare (RON)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.chitanta_valoare}
                      onChange={(e) => setFormData({ ...formData, chitanta_valoare: e.target.value })}
                      placeholder="0.00"
                      className="h-8 text-sm"
                      data-testid="input-chitanta-valoare"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Anulează</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-retrocedare-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {selectedItem ? "Salvează" : "Adaugă"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmați ștergerea</AlertDialogTitle>
            <AlertDialogDescription>
              Sigur doriți să ștergeți retrocedarea către {selectedItem?.avocat_name}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anulează</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Șterge
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
